/**
 * Offline context
 *
 * Connectivity and sync status are genuinely app-wide, which is what
 * Constitution VI reserves Context for. Per-screen data stays local — this
 * provider carries only what the whole app needs to reason about: whether we
 * are online, whether a sync is running, when data was last brought up to date
 * (FR-016), and how many local changes are still waiting.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { subscribe, sync as runSync } from './utils/offline/syncEngine';
import { getPlaybackSyncQueueInstance } from './utils/playbackSyncBootstrap';
import { getMeta } from './utils/offline/repository';
import {
  isOnline,
  subscribe as subscribeConnectivity,
  reportReachable,
} from './utils/offline/connectivity';

const OfflineContext = createContext({
  online: true,
  status: 'idle',
  lastUpdatedAt: null,
  pendingCount: 0,
  sync: () => {},
});

export const useOffline = () => useContext(OfflineContext);

const countPending = () => {
  try {
    return getPlaybackSyncQueueInstance()?.list()?.length ?? 0;
  } catch (e) {
    return 0;
  }
};

/**
 * When the device's copy was last refreshed.
 *
 * Prefers whichever is newer of the sync engine's `lastSyncedAt` and the
 * write-through cache's `lastCachedAt`. While the sync endpoint does not exist
 * only the latter is ever set — and reporting "not yet synced" over a library
 * the user can plainly see would be worse than saying nothing.
 */
const readLastCachedAt = async () => {
  try {
    return (await getMeta('lastCachedAt')) || null;
  } catch (e) {
    return null; // no account bound, or the store is unavailable
  }
};

const newest = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  return a > b ? a : b;
};

function OfflineProvider({ children }) {
  const [online, setOnline] = useState(isOnline());
  const [outcome, setOutcome] = useState({ status: 'idle', at: null });
  const [pendingCount, setPendingCount] = useState(0);
  const [lastCachedAt, setLastCachedAt] = useState(null);

  useEffect(() => {
    // The interface coming back is only a hint; assume reachable again and let
    // the next request prove otherwise.
    const goOnline = () => {
      reportReachable();
      setOnline(isOnline());
    };
    const goOffline = () => setOnline(isOnline());
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    // Requests are the authority on whether the server is actually there.
    const unsubscribe = subscribeConnectivity(() => setOnline(isOnline()));
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      unsubscribe();
    };
  }, []);

  useEffect(() => subscribe((next) => {
    setOutcome(next);
    setPendingCount(countPending());
  }), []);

  // The banner only shows while offline, so re-reading on that transition (and
  // on mount, for a cold start with no network) is enough — no need to watch
  // every cache write.
  useEffect(() => {
    let cancelled = false;
    readLastCachedAt().then((at) => {
      if (!cancelled) setLastCachedAt(at);
    });
    return () => { cancelled = true; };
  }, [online]);

  const sync = useCallback(() => runSync(), []);

  return (
    <OfflineContext.Provider
      value={{
        online,
        status: outcome.status,
        lastUpdatedAt: newest(outcome.at, lastCachedAt),
        pendingCount,
        sync,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
}

export default OfflineProvider;
